import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { defaultCustomerConfig, createAppTheme } from './theme/theme';
import ProductionApp from './components/ProductionApp';
import CompareApp from './components/CompareApp';
import './i18n/i18n';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Emotion caches for RTL and LTR
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });
const ltrCache = createCache({ key: 'muiltr', stylisPlugins: [prefixer] });

export default function App() {
  const { i18n } = useTranslation();
  const [config, setConfig] = useState(defaultCustomerConfig);
  const [configLoaded, setConfigLoaded] = useState(false);

  const isRtl = i18n.language === 'he';

  useEffect(() => {
    fetch(`${API_BASE}/api/config`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setConfig(prev => ({ ...prev, ...data })); })
      .catch(() => {})
      .finally(() => setConfigLoaded(true));
  }, []);

  // Set favicon dynamically per customer
  useEffect(() => {
    if (!config.favicon) return;
    const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = config.favicon;
    document.head.appendChild(link);
  }, [config.favicon]);

  const appTheme = useMemo(() => createAppTheme(config, isRtl ? 'rtl' : 'ltr'), [config, isRtl]);

  const toggleLang = () => {
    const next = isRtl ? 'en' : 'he';
    i18n.changeLanguage(next);
    document.documentElement.dir  = next === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = next;
  };

  return (
    <CacheProvider value={isRtl ? rtlCache : ltrCache}>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<ProductionApp config={config} toggleLang={toggleLang} />} />
            <Route path="/debug" element={
              !configLoaded
                ? null
                : config.testingMode
                  ? <ProductionApp config={config} toggleLang={toggleLang} debugMode={true} />
                  : <Navigate to="/" replace />
            } />
            <Route path="/compare" element={
              !configLoaded
                ? null
                : config.testingMode
                  ? <CompareApp />
                  : <Navigate to="/" replace />
            } />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </CacheProvider>
  );
}
